import React, { useEffect } from "react";
import {
  Modal,
  Form,
  Select,
  Button,
  notification,
  Divider,
  Switch,
} from "antd";
import { useMutation } from "@tanstack/react-query";
import { updateReview } from "../services/reviewsAPI";
import queryClient from "../queryClient";
import { useAuth } from "../hooks/useAuth";
import AttachmentsSection from "./AttachmentsSection";
import SimpleRichTextEditor from "./SimpleRichTextEditor";

const PROJECTS_REQUIRING_SUBMITTAL_RESPONSE_ATTACHMENTS = [
  "69f91c9305da08025dafa92d",
];

const SUBMITTAL_RESPONSE_ATTACHMENT_REQUIRED_ROLES = [
  "subcontractor",
  "consultant",
];

const UpdateReviewModal = ({
  visible,
  onClose,
  review,
  onCloseSubmittalModal = () => {},
}) => {
  const { selectedProject } = useAuth();
  const projectId = selectedProject.project_id;
  const [form] = Form.useForm();
  const [sendEmail, setSendEmail] = React.useState(true);
  const isOwnerOrManager =
    selectedProject.role === "owner" ||
    selectedProject.role === "project_manager";
  const isSubmittalDraftOrClosed = ["Draft", "Closed"].includes(
    review?.submittal_status
  );
  const emailNotificationsDisabled =
    !isOwnerOrManager || isSubmittalDraftOrClosed;
  const requiresAttachment =
    PROJECTS_REQUIRING_SUBMITTAL_RESPONSE_ATTACHMENTS.includes(projectId) &&
    SUBMITTAL_RESPONSE_ATTACHMENT_REQUIRED_ROLES.includes(selectedProject.role);

  const mutation = useMutation({
    mutationFn: ({ reviewId, reviewData }) =>
      updateReview(reviewId, reviewData),
    onSuccess: () => {
      queryClient.invalidateQueries(["submittals", projectId]);
      queryClient.invalidateQueries({ queryKey: ["submittals"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-open-submittals"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      onClose();
      form.resetFields();
      notification.success({ message: "Review updated successfully" });
      setTimeout(() => {
        onCloseSubmittalModal();
      }, 1000);
    },
    onError: (error) => {
      notification.error({
        message: "Failed to update review",
        description: error.message,
      });
    },
  });

  useEffect(() => {
    if (visible) {
      const initialSendEmail = !isSubmittalDraftOrClosed;
      form.setFieldsValue({
        comment: review.comment,
        status: review.status,
        attachments: review.attachments,
        email_notifications: initialSendEmail,
      });
      setSendEmail(initialSendEmail);
    }
  }, [visible, review, form, isSubmittalDraftOrClosed]);

  const handleUpdateReview = (values) => {
    const reviewData = {
      comment: values.comment,
      status: values.status,
      attachments: values.attachments,
      email_notifications: values.email_notifications,
    };
    mutation.mutate({ reviewId: review._id, reviewData });
  };

  return (
    <Modal
      title="Update Review"
      open={visible}
      onCancel={onClose}
      footer={null}
    >
      <p>
        <strong>Submittal Number:</strong> {review.submittal_number}
      </p>
      <p>
        <strong>Submittal Title:</strong> {review.submittal_title}
      </p>
      <p>
        <strong>Ball in Court:</strong>{" "}
        {review.submittal_ball_in_court
          .map((person) => person.username)
          .join(", ")}
      </p>
      <Divider />
      <Form form={form} onFinish={handleUpdateReview} layout="vertical">
        <Form.Item label="Comment" name="comment">
          <SimpleRichTextEditor
            placeholder="Enter your review comment..."
            aiEnhanceTarget="response"
          />
        </Form.Item>
        <Form.Item
          label="Status"
          name="status"
          rules={[{ required: true, message: "Please select a status!" }]}
        >
          <Select>
            <Select.Option value="Reviewed">Reviewed</Select.Option>
            <Select.Option value="Submitted">Submitted</Select.Option>
            <Select.Option value="Approved">Approved</Select.Option>
            <Select.Option value="Approved as Noted">
              Approved as Noted
            </Select.Option>
            <Select.Option value="Revise and Resubmit">
              Revise and Resubmit
            </Select.Option>
            <Select.Option value="Rejected">Rejected</Select.Option>
            <Select.Option value="Void">Void</Select.Option>
            <Select.Option value="Pending">Pending</Select.Option>
            <Select.Option value="For Record Only">
              For Record Only
            </Select.Option>
          </Select>
        </Form.Item>
        <Form.Item
          name="attachments"
          label="Attachments"
          className="w-full"
          rules={
            requiresAttachment
              ? [
                  {
                    validator: (_, value) =>
                      Array.isArray(value) && value.length > 0
                        ? Promise.resolve()
                        : Promise.reject(
                            new Error(
                              "Please add at least one attachment before updating the review.",
                            ),
                          ),
                  },
                ]
              : undefined
          }
        >
          <AttachmentsSection projectId={projectId} />
        </Form.Item>
        {isOwnerOrManager && (
          <Form.Item name="email_notifications" valuePropName="checked" noStyle>
            <div className="mb-6 flex items-center">
              <Switch
                checked={sendEmail}
                onChange={(checked) => {
                  setSendEmail(checked);
                  form.setFieldsValue({ email_notifications: checked });
                }}
                disabled={emailNotificationsDisabled}
              />
              <span className="ml-2">
                {isSubmittalDraftOrClosed
                  ? "Set submittal status to Open to enable emails"
                  : "Email Notifications"}
              </span>
            </div>
          </Form.Item>
        )}
        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={mutation.isPending}
            style={{ width: "100%" }}
          >
            {sendEmail ? "Update Review and Send Emails" : "Update Review"}
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default UpdateReviewModal;
